import { isApolloError } from '@apollo/client';
import React, { createContext, useContext } from 'react';
import { FieldContainer, FieldName } from 'v2/core/entities/fields';
import { QueryStatus } from 'v2/core/repository/edge-server/types';
import { default as useGetStaticFields } from 'v2/core/services/fields/useGetStaticFields';

interface FieldsContextInterface {
  FieldContainer?: Omit<
    FieldContainer,
    | 'Entity'
    | 'Theme'
    | 'Reason'
    | 'Metadata'
    | 'Language'
    | 'Source'
    | 'ReviewSentiment'
    | 'Category'
    | 'ID'
    | 'FeedbackRecords'
  >;
  status: QueryStatus;
  error?: Error;
  hasFields: {
    hasNonPredictionFields: boolean;
    hasStaticFields: boolean;
  };
}

const FieldsContext = createContext<FieldsContextInterface | undefined>(
  undefined,
);

const FieldsContextProvider = ({ children }: { children: React.ReactNode }) => {
  const { fields: staticFields } = useGetStaticFields();

  const fields:
    | Omit<
        FieldContainer,
        | 'Entity'
        | 'Theme'
        | 'Reason'
        | 'Metadata'
        | 'Language'
        | 'Source'
        | 'ReviewSentiment'
        | 'Category'
        | 'ID'
        | 'FeedbackRecords'
      >
    | undefined = staticFields
    ? {
        ...staticFields,
      }
    : undefined;

  return (
    <FieldsContext.Provider
      value={{
        status: QueryStatus.SUCCESS_COMPLETED,
        FieldContainer: fields,
        hasFields: {
          hasNonPredictionFields: false,
          hasStaticFields: !!staticFields,
        },
      }}
    >
      {children}
    </FieldsContext.Provider>
  );
};

const contextErrorCheck = (context: FieldsContextInterface) => {
  if (context.error) {
    if (isApolloError(context.error)) {
      throw context.error;
    }

    throw Error(context.error.toString());
  }
  if (context.status !== QueryStatus.SUCCESS_COMPLETED) {
    return;
  }
  if (context.FieldContainer) {
    return;
  }

  const missingFields = Object.entries(context.hasFields)
    .filter(([, hasField]) => !hasField)
    .map(([field]) => field)
    .join(', ');

  throw Error(
    `field queries completed but has missing fields: ${missingFields}`,
  );
};

const useGetField = <
  K extends Exclude<
    FieldName,
    | 'Entity'
    | 'Reason'
    | 'Theme'
    | 'Metadata'
    | 'Language'
    | 'Source'
    | 'ReviewSentiment'
    | 'Category'
    | 'ID'
    | 'FeedbackRecords'
  >,
>(
  f: K,
): [QueryStatus, FieldContainer[K] | undefined] => {
  const context = useContext(FieldsContext);
  if (!context) {
    throw Error('useGetField called without fields context being provided');
  }
  contextErrorCheck(context);
  // already checked for errors and undefined behaviour
  return [context.status, context.FieldContainer?.[f]];
};

export { FieldsContextProvider, useGetField };
